// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { ShareNetwork } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ShareAskPunttChatDialog } from "../../components/ask-puntt/ShareAskPunttChatDialog";
import { AskPunttArtifactProvider } from "../../components/ask-puntt-review";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { buildMentionComposerMembers } from "../../utils/buildMentionComposerMembers";
import { useAppSelector } from "../../utils/hooks";
import { useMarkAskPunttChatRead } from "../../utils/queries/askPuntt";
import {
  useAskPunttProject,
  useProjectReviews,
} from "../../utils/queries/askPunttProjects";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_HOME_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import {
  collectRailContent,
  getReviewScriptedProgressMode,
  hasRailContent,
} from "../rail/collectRailContent";
import { mergeScriptedRailProgress } from "../rail/mergeScriptedRailProgress";
import { type AskV2RailSectionId } from "../rail/types";
import { useAskV2RailReviewAutoOpen } from "../rail/useAskV2RailReviewAutoOpen";
import { useAskV2ScriptedProgress } from "../rail/useAskV2ScriptedProgress";
import { AskV2ReviewWorkspace } from "../review/AskV2ReviewWorkspace";
import { useAskV2ReviewArtifactContext } from "../review/useAskV2ReviewArtifactContext";
import { useAskV2ReviewSidebarAutoCollapse } from "../review/useAskV2ReviewSidebarAutoCollapse";
import { useAskV2ReviewWorkspace } from "../review/useAskV2ReviewWorkspace";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import {
  useAskV2Transport,
  type AskV2TransportApi,
} from "../transport/useAskV2Transport";

type AskV2ChatPageProps = {
  chatId: string;
  reviewId?: string;
  commentId?: string;
};

// The four AskV2TransportApi members that reach the network — send, retry,
// submitEdit and abort — are each a request the backend answers with 403 for a
// read-only viewer, and `gatedTransport` below swaps all four for this no-op.
// Everything else on the API is local composer/edit state that never leaves the
// client. Resolving rather than rejecting keeps the local thread honest: no
// optimistic bubble, no failed-send retry affordance.
function refuseWrite(): Promise<void> {
  return Promise.resolve();
}

/**
 * Swap every network-reaching member of `AskV2TransportApi` for `refuseWrite`
 * when the viewer may not post. Exported so each override is pinned on its
 * own in AskV2ChatPage.test.tsx: `abort` was missing from this list for a
 * whole review cycle because only `send` was ever exercised through the page,
 * and `retry` / `submitEdit` have no UI path a page-level test can reach.
 */
export function gateTransportWrites(
  transport: AskV2TransportApi,
  canInteract: boolean,
): AskV2TransportApi {
  if (canInteract) return transport;
  return {
    ...transport,
    send: refuseWrite,
    retry: refuseWrite,
    submitEdit: refuseWrite,
    // Stop is a write too: abortInFlight() POSTs abortMessage (403 here) and
    // its finally block then marks the turn "aborted" locally whether or not
    // the call succeeded — so an ungated Stop shows a read-only viewer a
    // cancelled turn the server is in fact still running.
    abort: refuseWrite,
  };
}

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ChatPage({
  chatId,
  reviewId,
  commentId,
}: AskV2ChatPageProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const currentUser = useAppSelector((state) => state.auth.value);
  const currentUserId = currentUser?.userID;
  const currentUserName =
    currentUser?.name?.trim() || currentUser?.email?.trim();
  const transport = useAskV2Transport({ chatId, presentation: "chat" });
  const chat = transport.historyChat;
  const title = transport.chatTitle?.trim() || t("askV2.pages.chat.title");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const [shareOpen, setShareOpen] = useState(false);
  const workspace = useAskV2ReviewWorkspace({
    scope: { kind: "chat", chatId },
    reviewId,
    commentId,
  });
  const projectId = chat?.projectId ?? null;
  const projectQuery = useAskPunttProject(projectId);
  const project = projectQuery.data?.project;
  const reviewsQuery = useProjectReviews(projectId);
  const projectMembers = project?.members ?? [];
  const allowListMembers = chat?.allowList ?? [];
  const chatParticipants = useMemo(
    () => buildMentionComposerMembers(projectMembers, allowListMembers),
    [projectMembers, allowListMembers],
  );
  // Shared chats have more than one human commenter: the owner plus everyone
  // on the allow-list (and the project roster when the chat is project-linked).
  // Without this map the review pane falls back to the generic "Member" label
  // for every comment the viewer did not write.
  const authorNameByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const member of chatParticipants) {
      const label = member.name?.trim() || member.email?.trim();
      if (member._id && label) map[member._id] = label;
    }
    return map;
  }, [chatParticipants]);
  const isChatOwner =
    !!chat &&
    !!currentUserId &&
    chat.userId === currentUserId &&
    chat.role !== "shared";
  // Whether the server lets this requester post into the chat (owner or
  // allowList member). False for MG cross-user readers and non-member
  // viewers, whose sends the backend rejects with 403 — the composer is
  // locked before a doomed send. Fails OPEN when the field is absent
  // (older backend / cached payloads): the lock only engages on the
  // server's explicit say-so.
  const canInteract = chat?.canInteract ?? true;
  // Locking the composer is not enough: the review workspace renders its OWN
  // AskV2Thread out of this transport (answer forms included), so a `readOnly`
  // prop we hand the thread below never reaches that surface — and review mode
  // is reachable straight from the URL. Gate the transport once, here, instead
  // of at each render site, so no consumer of AskV2TransportApi can re-open the
  // hole by forgetting a prop.
  //
  // Scope: AskV2TransportApi members only. The review workspace's own
  // react-query writes never flow through the transport and stay uncovered —
  // `createReviewComment` (from `useCreateAskPunttReviewComment`, behind
  // `handleSubmitHumanComment` / `handleJudgeComment` in
  // AskV2ReviewWorkspace.tsx) still POSTs for a read-only viewer who opens
  // review mode straight from the URL — and that POST SUCCEEDS. Christo
  // authorizes that route on enterprise scope alone: `createReviewComment`
  // (services/christo/app/handlers/catalyst/askPunttReview.ts) resolves the
  // review through `loadReview`, whose whole filter is
  // `{ _id: reviewId, enterpriseId }`, with no owner / allowList /
  // canInteract check anywhere in that handler; `userId` is recorded as
  // `createdBy` only, so the write is attributable but not authorized. Any
  // enterprise member can therefore comment on any review in the enterprise,
  // on or off the chat. Hiding the composer is the ONLY thing stopping a
  // read-only viewer on this surface — do not read it as server-refused.
  // Closing it for real needs a membership probe in
  // `createReviewComment` / `updateReviewComment` plus a `readOnly` prop on
  // AskV2ReviewWorkspace; neither file is in this PR's diff.
  const gatedTransport = gateTransportWrites(transport, canInteract);
  const mentionComposerMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        isChatOwner ? projectMembers : [],
        allowListMembers,
      ),
    [allowListMembers, isChatOwner, projectMembers],
  );
  const chatSummonBar = (
    <AskV2SummonBar
      transport={gatedTransport}
      mentionMembers={mentionComposerMembers}
      rosterMembers={isChatOwner ? projectMembers : []}
      enableDirectoryMentions={isChatOwner}
      composerAriaLabel={t("replyToChat")}
      readOnly={!canInteract}
    />
  );
  const progressMode = getReviewScriptedProgressMode({
    chat: transport.historyChat,
    messages: transport.historyMessages,
    reviews: reviewsQuery.data,
  });
  const scriptedProgress = useAskV2ScriptedProgress({
    chatId,
    mode: progressMode,
  });
  const setRailOpenStable = useCallback(
    (open: boolean) => setRailOpen(open),
    [],
  );
  const setSectionOpenStable = useCallback(
    (
      updater: (
        current: Record<AskV2RailSectionId, boolean>,
      ) => Record<AskV2RailSectionId, boolean>,
    ) => setSectionOpen(updater),
    [],
  );
  useAskV2RailReviewAutoOpen({
    mode: progressMode,
    setRailOpen: setRailOpenStable,
    setSectionOpen: setSectionOpenStable,
  });
  const railContent = useMemo(() => {
    const base = collectRailContent({
      project,
      chat: transport.historyChat,
      messages: transport.historyMessages,
      reviews: reviewsQuery.data,
    });
    return mergeScriptedRailProgress(base, scriptedProgress);
  }, [
    project,
    reviewsQuery.data,
    scriptedProgress,
    transport.historyChat,
    transport.historyMessages,
  ]);
  const railHasContent = hasRailContent(railContent);
  const showRailToggle = !workspace.isReviewOpen && railHasContent;
  const showRail = showRailToggle && railOpen;
  const versionOptions = useMemo(() => {
    const reviewIds = transport.historyChat?.reviewIds ?? [];
    return reviewIds.map((id, index) => ({
      reviewId: id,
      title: t("askV2.review.versionLabel", { number: index + 1 }),
    }));
  }, [transport.historyChat?.reviewIds, t]);
  const artifactContext = useAskV2ReviewArtifactContext({
    workspace,
    messages: transport.historyMessages,
  });
  useAskV2ReviewSidebarAutoCollapse(workspace.isReviewOpen);

  const markChatRead = useMarkAskPunttChatRead(chatId);
  const markChatReadMutate = markChatRead.mutate;
  const tailMessage = transport.historyMessages.at(-1);
  const tailMessageId = tailMessage?._id ?? null;
  // why: V2 never sent a read receipt, so the sidebar unread dot and the home
  // @mention task for a chat outlived the reader opening it. One receipt per
  // settled tail; none while this reader's own turn is still posting or the
  // assistant tail is still pending, because completion bumps lastMessageAt
  // server-side and would flip the chat back to unread.
  //
  // Not transport.inFlight: deriveInFlight() calls any user-role tail
  // outstanding (warhol has no pending assistant row), so a chat whose last
  // persisted message is a user turn -- an aborted or errored generation, or an
  // @mention nobody answered -- reads as permanently in flight and never got a
  // receipt at all, leaving it unread however often the reader opened it.
  // Behavior change: such a chat now earns its receipt once this reader's own
  // turn has settled, so it stops showing as unread on every later open.
  const replyOutstanding =
    transport.sending ||
    (tailMessage?.role === "assistant" && tailMessage.status === "pending");
  const tailReadReceiptKey = chat
    ? `${tailMessageId ?? "chat"}:${chat.lastMessageAt ?? ""}`
    : null;
  const lastMarkedReadReceiptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tailReadReceiptKey || replyOutstanding) return;
    if (lastMarkedReadReceiptRef.current === tailReadReceiptKey) return;
    lastMarkedReadReceiptRef.current = tailReadReceiptKey;
    markChatReadMutate(
      tailMessageId ? { messageId: tailMessageId } : undefined,
    );
  }, [markChatReadMutate, replyOutstanding, tailMessageId, tailReadReceiptKey]);

  return (
    <>
      <AskV2ConversationShell
        testId="ask-v2-chat-page"
        fullBleed={workspace.isReviewOpen}
        scrollResetKey={chatId}
        scrollDeps={
          workspace.isReviewOpen ? undefined : [transport.displayMessages]
        }
        autoScrollEnabled={!transport.inFlight}
        scrollSentSignal={transport.sending}
        onAddQuotedContext={
          workspace.isReviewOpen ? undefined : transport.addQuotedContext
        }
        header={
          <PageHeader>
            <BreadcrumbHeaderTrail className="min-w-0 flex-1 overflow-hidden">
              <BreadcrumbHeaderRoot
                to={ASK_V2_HOME_PATH}
                label={tCommon("nav.askPuntt")}
                testId="ask-v2-chat-breadcrumb-root"
              />
              <span
                className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
                data-testid="ask-v2-chat-title"
              >
                {title}
              </span>
              <span
                className={`${BREADCRUMB_HEADER_CHIP_CLASS} sr-only`}
                data-testid="ask-v2-chat-id"
              >
                {chatId}
              </span>
            </BreadcrumbHeaderTrail>
            {isChatOwner ? (
              <Button
                data-testid="ask-v2-chat-share"
                aria-label={t("shareChat")}
                size="1"
                variant="soft"
                color="gray"
                className="ml-auto shrink-0 cursor-pointer"
                onClick={() => setShareOpen(true)}
              >
                <ShareNetwork size={14} aria-hidden="true" />
                <Text size="1">{t("share")}</Text>
              </Button>
            ) : null}
            {showRailToggle ? (
              <AskV2RailToggle
                open={railOpen}
                onToggle={() => setRailOpen((current) => !current)}
              />
            ) : null}
          </PageHeader>
        }
        composer={workspace.isReviewOpen ? undefined : chatSummonBar}
        rail={
          showRail ? (
            <AskV2RightRail
              content={railContent}
              sectionOpen={sectionOpen}
              onReviewOpen={workspace.openReview}
              onSectionOpenChange={(id, open) =>
                setSectionOpen((current) => ({ ...current, [id]: open }))
              }
            />
          ) : null
        }
      >
        {workspace.isReviewOpen ? (
          <AskV2ReviewWorkspace
            workspace={workspace}
            transport={gatedTransport}
            versionOptions={versionOptions}
            feedPresentation="chat"
            feedActorName={currentUserName}
            feedMembers={mentionComposerMembers}
            currentUserId={currentUserId}
            authorNameByUserId={authorNameByUserId}
            artifactContext={artifactContext}
            composer={chatSummonBar}
          />
        ) : (
          <AskPunttArtifactProvider value={artifactContext}>
            {transport.historyLoading && transport.messages.length === 0 ? (
              <AskV2ThreadLoading />
            ) : transport.historyError && transport.messages.length === 0 ? (
              <Text
                as="p"
                size="2"
                color="gray"
                data-testid="ask-v2-thread-error"
                className="py-8 text-center"
              >
                {t("askV2.pages.chat.loadError")}
              </Text>
            ) : (
              <AskV2Thread
                transport={gatedTransport}
                presentation="chat"
                feedMembers={mentionComposerMembers}
                currentUserId={currentUserId}
                // The answer form sends straight through the transport, so it
                // needs the same gate the composer gets above — otherwise a
                // read-only viewer answering a question form still fires the
                // 403 this page locks the composer to avoid.
                readOnly={!canInteract}
              />
            )}
          </AskPunttArtifactProvider>
        )}
      </AskV2ConversationShell>
      {chat ? (
        <ShareAskPunttChatDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          chatId={chatId}
          chatTitle={chat.title}
          members={allowListMembers}
          projectId={projectId}
          projectName={projectQuery.data?.project?.name}
          projectMembers={projectMembers}
        />
      ) : null}
    </>
  );
}
